import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Users, TrendingUp, DollarSign, FileText, Eye, MapPin, Map, UserCheck } from 'lucide-react';
import axios from 'axios';
import { toast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AnalyticsDashboard = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
    // Track this page visit
    axios.post(`${BACKEND_URL}/api/analytics/visit`, null, { params: { page: 'admin_dashboard' } });
  }, []);

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/analytics`);
      setAnalytics(response.data);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load analytics",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading || !analytics) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const mainStats = [
    {
      label: 'Total Users',
      value: analytics.total_users.toLocaleString(),
      icon: Users,
      color: 'bg-blue-100 text-blue-600',
      change: '+12%'
    },
    {
      label: 'Site Visits (30d)',
      value: analytics.site_visits_30days.toLocaleString(),
      icon: Eye,
      color: 'bg-purple-100 text-purple-600',
      change: '+8%'
    },
    {
      label: 'Total Predictions',
      value: analytics.total_predictions.toLocaleString(),
      icon: FileText,
      color: 'bg-green-100 text-green-600',
      change: '+15%'
    },
    {
      label: 'Total Referrals',
      value: analytics.total_referrals.toLocaleString(),
      icon: UserCheck,
      color: 'bg-orange-100 text-orange-600',
      change: '+20%'
    }
  ];

  const secondaryStats = [
    {
      label: 'Earnings Distributed',
      value: `₹${analytics.total_earnings_distributed.toLocaleString()}`,
      icon: DollarSign,
      color: 'bg-emerald-100 text-emerald-600'
    },
    {
      label: 'Pending Withdrawals',
      value: analytics.pending_withdrawals.toLocaleString(),
      icon: TrendingUp,
      color: 'bg-yellow-100 text-yellow-600'
    },
    {
      label: 'Approved Withdrawals',
      value: analytics.approved_withdrawals.toLocaleString(),
      icon: TrendingUp,
      color: 'bg-green-100 text-green-600'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Main Statistics */}
      <div className="grid md:grid-cols-4 gap-6">
        {mainStats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card key={index} className="hover:shadow-lg transition-shadow">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-slate-600">
                  {stat.label}
                </CardTitle>
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${stat.color}`}>
                  <Icon className="w-5 h-5" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex items-baseline justify-between">
                  <p className="text-3xl font-bold">{stat.value}</p>
                  {stat.change && (
                    <span className="text-sm text-green-600 font-medium">
                      {stat.change}
                    </span>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Secondary Statistics */}
      <div className="grid md:grid-cols-3 gap-6">
        {secondaryStats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card key={index}>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">{stat.label}</p>
                    <p className="text-2xl font-bold mt-2">{stat.value}</p>
                  </div>
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${stat.color}`}>
                    <Icon className="w-6 h-6" />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Predictions by Exam Type */}
      {analytics.predictions_by_exam && analytics.predictions_by_exam.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Predictions by Exam Type</CardTitle>
            <CardDescription>Distribution of predictions across different exams</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {analytics.predictions_by_exam.map((exam, index) => {
                const percentage = (exam.count / analytics.total_predictions) * 100;
                return (
                  <div key={index}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">{exam._id || 'Unknown'}</span>
                      <span className="text-sm text-slate-600">
                        {exam.count} ({percentage.toFixed(1)}%)
                      </span>
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-2">
                      <div
                        className="bg-gradient-to-r from-blue-500 to-cyan-500 h-2 rounded-full transition-all"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Top Referrers */}
      {analytics.top_referrers && analytics.top_referrers.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Top Referrers</CardTitle>
            <CardDescription>Users with most referrals</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {analytics.top_referrers.map((referrer, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-cyan-500 rounded-full flex items-center justify-center text-white font-bold">
                      {index + 1}
                    </div>
                    <span className="font-medium">User ID: {referrer._id.substring(0, 8)}...</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-600">{referrer.count} referrals</span>
                    <span className="text-sm font-semibold text-green-600">
                      ₹{(referrer.count * 200).toLocaleString()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Activities */}
      {analytics.recent_activities && analytics.recent_activities.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Recent Activities</CardTitle>
            <CardDescription>Latest predictions and user activities</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {analytics.recent_activities.map((activity, index) => (
                <div key={index} className="flex items-center justify-between p-3 border-b last:border-b-0">
                  <div>
                    <p className="font-medium">New Prediction</p>
                    <p className="text-sm text-slate-600">
                      {activity.exam_type} - Rank {activity.rank}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-slate-600">
                      {activity.created_at ? new Date(activity.created_at).toLocaleDateString() : 'N/A'}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* City & State Information Note */}
      <Card className="bg-amber-50 border-amber-200">
        <CardContent className="pt-6">
          <div className="flex gap-3">
            <MapPin className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-amber-900 mb-1">Geographic Analytics</p>
              <p className="text-sm text-amber-700">
                City and state-wise breakdown will be available once user location data is collected during registration. 
                Consider adding city/state fields to the user registration form for detailed geographic analytics.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AnalyticsDashboard;
